import {getSectionDebriefItemPath} from 'course/section/postSectionSurveys/sectionDebriefRoadmapItem';
import {getSectionFeedbackItemPath} from 'course/section/slSectionFeedback/sectionFeedbackRoadmapItem';
import {getMathAssignmentPath, getOneOfChoices} from './roadmapOneOf';
import {ROADMAP, type RoadmapItem} from './roadmapSchema';

/** Which lesson viewer a Lesson item opens in. */
export type LessonRouteKey = 'learn' | 'training';

/**
 * Item types whose link *is* their `urlId` (the admin typed a URL when
 * creating the item). Editing the link for these rewrites `urlId`; every
 * other type derives its link from ids and uses `href` as an override.
 *
 * @param itemType - The item's `itemType`.
 * @return `true` when the item's link is stored in `urlId`.
 * @throws Never.
 */
export function isLinkStoredInUrlId(itemType: string): boolean {
  return (
    itemType === ROADMAP.UNTIED_LINK.key ||
    itemType === ROADMAP.COMPLETION_LINK.key ||
    itemType === ROADMAP.AUTO_CHECK.key
  );
}

/**
 * The link an item resolves to by type, ignoring any `href` override. This is
 * what students are sent to when the admin has not customised the link.
 *
 * @param args.item - The roadmap item.
 * @param args.courseId - The course the roadmap belongs to.
 * @param args.lessonKey - Which lesson viewer Lesson items open in. Defaults
 *   to `learn` (the student viewer); teaching roadmaps pass `training`.
 * @return An absolute in-app path, or an external URL for link-type items.
 * @throws Never.
 */
export function getDefaultRoadmapItemHref({
  item,
  courseId,
  lessonKey = 'learn',
}: {
  item: RoadmapItem;
  courseId: string;
  lessonKey?: LessonRouteKey;
}): string {
  const urlId = item.urlId ?? '';
  switch (item.itemType) {
    case ROADMAP.LESSON.key:
      return `/${courseId}/${lessonKey}/${urlId}`;
    case ROADMAP.ASSIGNMENT.key:
      return `/${courseId}/ide/a/${urlId}`;
    case ROADMAP.MATH_ASSIGNMENT.key:
      return `/${courseId}/mide/a/${urlId}`;
    case ROADMAP.ONE_OF.key: {
      // The click offers the choices; the link is where the first one lives,
      // for anything that shows or follows an item's link without clicking.
      const [first] = getOneOfChoices(item);
      return first === undefined
        ? `/${courseId}`
        : getMathAssignmentPath({courseId, assignmentId: first});
    }
    case ROADMAP.UNTIED_LINK.key:
    case ROADMAP.COMPLETION_LINK.key:
    case ROADMAP.AUTO_CHECK.key:
      return urlId;
    case ROADMAP.SECTION.key:
      return `/${courseId}/section`;
    case ROADMAP.SECTION_SHADOW.key:
      return `/${courseId}/shadowing`;
    case ROADMAP.SECTION_DEBRIEF.key:
      // The week-only route resolves the SL's own or covered section.
      return getSectionDebriefItemPath({
        courseId,
        completionId: item.completionId,
      });
    case ROADMAP.SECTION_FEEDBACK.key:
      return getSectionFeedbackItemPath({
        courseId,
        completionId: item.completionId,
      });
    default:
      return `/${courseId}`;
  }
}

/**
 * The link an item actually opens: the item's `href` override when set and
 * non-blank, otherwise {@link getDefaultRoadmapItemHref}.
 *
 * @param args.item - The roadmap item.
 * @param args.courseId - The course the roadmap belongs to.
 * @param args.lessonKey - Which lesson viewer Lesson items open in. Defaults
 *   to `learn`.
 * @return An absolute in-app path, or an external URL.
 * @throws Never.
 */
export function getRoadmapItemHref({
  item,
  courseId,
  lessonKey = 'learn',
}: {
  item: RoadmapItem;
  courseId: string;
  lessonKey?: LessonRouteKey;
}): string {
  const override = typeof item.href === 'string' ? item.href.trim() : '';
  if (override !== '') {
    return override;
  }
  return getDefaultRoadmapItemHref({item, courseId, lessonKey});
}

/**
 * Applies an admin's edited link to an item. Link-type items store it in
 * `urlId`; other types store it as an `href` override, which is dropped again
 * when the edited link is blank or equals the type's default.
 *
 * @param args.item - The item before the edit.
 * @param args.nextHref - The link as typed by the admin.
 * @param args.courseId - The course the roadmap belongs to.
 * @param args.lessonKey - Which lesson viewer Lesson items open in. Defaults
 *   to `learn`.
 * @return A new item object with the link applied.
 * @throws Never.
 */
export function withEditedHref({
  item,
  nextHref,
  courseId,
  lessonKey = 'learn',
}: {
  item: RoadmapItem;
  nextHref: string;
  courseId: string;
  lessonKey?: LessonRouteKey;
}): RoadmapItem {
  const trimmed = nextHref.trim();
  if (isLinkStoredInUrlId(item.itemType)) {
    return {...item, urlId: trimmed};
  }
  const next: RoadmapItem = {...item};
  const defaultHref = getDefaultRoadmapItemHref({item, courseId, lessonKey});
  if (trimmed === '' || trimmed === defaultHref) {
    delete next.href;
  } else {
    next.href = trimmed;
  }
  return next;
}
